// ── Enhanced Scoreboard ──
// CS:GO-style scoreboard with team sections, ADR, HS%, flash assists,
// utility damage, MVPs, weapon icons, team economy, and color-coded stats.

import {
  type EnhancedScoreboardEntry,
  sortScoreboardEntries,
  teamEconomyTotal,
  adrColor,
} from './scoreboard-stats';
import { isMobile } from '../../core/device';

// ── Spectator mobile control callbacks ──
// BUG-PARITY-H3: Mobile spectators have no way to switch targets (desktop
// uses ArrowLeft/Right + LMB/RMB). The scoreboard renders a touch-friendly
// PREV / MODE / NEXT row at the top while in spectating mode on mobile.

export interface SpectatorCallbacks {
  prev: () => void;
  next: () => void;
  cycleMode: () => void;
}

// ── ScoreboardEntry interface ──
// Extended with optional fields for backward compatibility.
// Callers that only provide name/kills/deaths/ping/isLocal still work.

export interface ScoreboardEntry {
  name: string;
  kills: number;
  deaths: number;
  ping: number;
  isLocal: boolean;
  // Enhanced fields (optional for backward compat)
  assists?: number;
  team?: number;         // 0=unassigned, 1=T, 2=CT
  adr?: number;
  hsPercent?: number;
  flashAssists?: number;
  utilityDamage?: number;
  mvps?: number;
  money?: number;
  weapon?: string;
  // UI-H5: highlight-reel achievements rendered as inline badges.
  clutches?: number;
  aces?: number;
  longestShot?: number;
}

/** Callback signature for player report actions. */
export type ReportCallback = (playerName: string, reason: string) => void;

/** Valid report reasons matching server-side report.go constants. */
export const REPORT_REASONS = ['cheating', 'toxicity', 'afk', 'griefing', 'other'] as const;
export type ReportReason = typeof REPORT_REASONS[number];

// ── Column definitions for responsive layout ──

interface ColumnDef {
  label: string;
  /** CSS flex shorthand or fixed width */
  width: string;
  /** Minimum width in px */
  minWidth: number;
  align: 'left' | 'center' | 'right';
}

const COLUMNS: ColumnDef[] = [
  { label: '',      width: '24px',  minWidth: 24,  align: 'center' }, // weapon icon
  { label: 'Name',  width: '1 1 0', minWidth: 100, align: 'left' },
  { label: 'K',     width: '40px',  minWidth: 32,  align: 'center' },
  { label: 'A',     width: '40px',  minWidth: 32,  align: 'center' },
  { label: 'D',     width: '40px',  minWidth: 32,  align: 'center' },
  { label: 'ADR',   width: '48px',  minWidth: 40,  align: 'center' },
  { label: 'HS%',   width: '48px',  minWidth: 40,  align: 'center' },
  { label: 'FA',    width: '36px',  minWidth: 28,  align: 'center' }, // flash assists
  { label: 'UD',    width: '40px',  minWidth: 32,  align: 'center' }, // utility damage
  { label: 'Ping',  width: '48px',  minWidth: 40,  align: 'center' },
  // UI-H5: highlight-reel badge column — MVP / 1vN / Ace / Longest shot.
  { label: 'Hi',    width: '120px', minWidth: 96,  align: 'center' },
  { label: '',      width: '80px',  minWidth: 72,  align: 'center' }, // mute/report actions
];

// ── Weapon icon unicode/emoji map for common weapons ──
// Uses simple text abbreviations since we can't load sprites in the scoreboard.

const WEAPON_SHORT: Record<string, string> = {
  glock: 'GL', usp: 'USP', p250: 'P2', deagle: 'DE', fiveseven: '57', tec9: 'T9',
  ak47: 'AK', m4a4: 'M4', m4a1s: 'M4S', famas: 'FA', galil: 'GAL',
  aug: 'AUG', sg553: 'SG', awp: 'AWP', ssg08: 'SSG',
  mp9: 'MP9', mac10: 'MAC', p90: 'P90',
  nova: 'NOV', knife: 'KN',
  hegrenade: 'HE', flashbang: 'FB', smoke: 'SM', molotov: 'ML', incendiary: 'INC',
};

// ── Team colors ──
const TEAM_COLORS: Record<number, { bg: string; header: string; label: string }> = {
  1: { bg: 'rgba(222, 163, 67, 0.08)', header: 'rgba(222, 163, 67, 0.25)', label: 'TERRORISTS' },
  2: { bg: 'rgba(93, 160, 224, 0.08)', header: 'rgba(93, 160, 224, 0.25)', label: 'COUNTER-TERRORISTS' },
};

// ── Helper: convert ScoreboardEntry to EnhancedScoreboardEntry ──

function toEnhanced(e: ScoreboardEntry): EnhancedScoreboardEntry {
  return {
    name: e.name,
    kills: e.kills,
    deaths: e.deaths,
    assists: e.assists ?? 0,
    ping: e.ping,
    isLocal: e.isLocal,
    team: e.team ?? 0,
    adr: e.adr ?? 0,
    hsPercent: e.hsPercent ?? 0,
    flashAssists: e.flashAssists ?? 0,
    utilityDamage: e.utilityDamage ?? 0,
    mvps: e.mvps ?? 0,
    money: e.money ?? 0,
    weapon: e.weapon ?? 'knife',
    clutches: e.clutches ?? 0,
    aces: e.aces ?? 0,
    longestShot: e.longestShot ?? 0,
  };
}

// ── Scoreboard class ──

export class Scoreboard {
  private el: HTMLElement;
  private bodyEl: HTMLElement;
  private visible: boolean = false;
  private mutedPlayers: Set<string> = new Set();
  private reportedPlayers: Set<string> = new Set();
  private matchState: string = 'playing';
  private roundNumber: number | null = null;

  /** Optional callback invoked when a player is reported. */
  onReport: ReportCallback | null = null;

  /** BUG-PARITY-H3: callbacks for mobile spectator target/mode switching. */
  private spectatorCallbacks: SpectatorCallbacks | null = null;
  /** Whether the local player is currently spectating (drives mobile button row). */
  private spectating: boolean = false;

  constructor() {
    this.el = document.getElementById('scoreboard')!;
    this.bodyEl = document.getElementById('scoreboard-body')!;
    this.injectStyles();
  }

  /**
   * BUG-PARITY-H3: Wire spectator target/mode switching callbacks.
   * game.ts calls this once on init; the scoreboard renders a touch-friendly
   * PREV / MODE / NEXT row at the top while spectating on mobile.
   */
  setSpectatorCallbacks(callbacks: SpectatorCallbacks | null): void {
    this.spectatorCallbacks = callbacks;
  }

  /** Returns true if the given player name is muted. */
  isPlayerMuted(playerName: string): boolean {
    return this.mutedPlayers.has(playerName);
  }

  /** Returns the set of muted player names (read-only copy). */
  getMutedPlayers(): ReadonlySet<string> {
    return this.mutedPlayers;
  }

  update(entries: ScoreboardEntry[]): void {
    const enhanced = entries.map(toEnhanced);

    // Split by team
    const tPlayers = enhanced.filter(e => e.team === 1);
    const ctPlayers = enhanced.filter(e => e.team === 2);
    const unassigned = enhanced.filter(e => e.team !== 1 && e.team !== 2);

    // Sort each group
    const tSorted = sortScoreboardEntries(tPlayers);
    const ctSorted = sortScoreboardEntries(ctPlayers);
    const unSorted = sortScoreboardEntries(unassigned);

    // Clear
    this.bodyEl.innerHTML = '';

    // BUG-PARITY-H3: render mobile spectator controls row at the top.
    if (this.spectating && isMobile() && this.spectatorCallbacks) {
      this.renderSpectatorControls();
    }

    // Render T section
    if (tSorted.length > 0) {
      this.renderTeamSection(tSorted, 1);
    }

    // Render CT section
    if (ctSorted.length > 0) {
      this.renderTeamSection(ctSorted, 2);
    }

    // Render unassigned (fallback for pre-team-select or legacy callers)
    if (unSorted.length > 0) {
      this.renderTeamSection(unSorted, 0);
    }

    this.renderSurrenderFooter();
  }

  /** Provide match context for footer actions; if no round is known, playing state is enough. */
  setMatchContext(state: string, roundNumber?: number): void {
    this.matchState = state;
    this.roundNumber = Number.isFinite(roundNumber) ? roundNumber! : null;
    this.bodyEl.querySelector('.sb-footer-row')?.remove();
    if (this.bodyEl.childElementCount > 0) {
      this.renderSurrenderFooter();
    }
  }

  /**
   * BUG-PARITY-H3: Render a touch-friendly PREV / MODE / NEXT button row
   * for mobile spectators. Wired to {@link SpectatorCallbacks}.
   */
  private renderSpectatorControls(): void {
    const callbacks = this.spectatorCallbacks;
    if (!callbacks) return;

    const row = document.createElement('tr');
    row.className = 'sb-spec-controls';
    const td = document.createElement('td');
    td.colSpan = COLUMNS.length;
    td.style.cssText = 'padding:6px 8px;background:rgba(0,0,0,0.45);border-bottom:1px solid #444;';

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;gap:6px;justify-content:center;align-items:center;';

    const mkBtn = (
      label: string,
      title: string,
      onClick: () => void,
      cls: string,
    ): HTMLButtonElement => {
      const btn = document.createElement('button');
      btn.className = `sb-spec-btn ${cls}`;
      btn.type = 'button';
      btn.textContent = label;
      btn.title = title;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        onClick();
      });
      // Prevent the touch from also being interpreted as a look gesture
      // when the scoreboard overlay is open.
      btn.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
      return btn;
    };

    wrapper.appendChild(mkBtn('◀ PREV', 'Previous spectator target', callbacks.prev, 'sb-spec-prev'));
    wrapper.appendChild(mkBtn('MODE', 'Cycle spectator camera mode (FPP/TPP/Free)', callbacks.cycleMode, 'sb-spec-mode'));
    wrapper.appendChild(mkBtn('NEXT ▶', 'Next spectator target', callbacks.next, 'sb-spec-next'));

    td.appendChild(wrapper);
    row.appendChild(td);
    this.bodyEl.appendChild(row);
  }

  private renderTeamSection(entries: EnhancedScoreboardEntry[], team: number): void {
    const colors = TEAM_COLORS[team] ?? { bg: 'rgba(128,128,128,0.08)', header: 'rgba(128,128,128,0.2)', label: 'UNASSIGNED' };

    // UI-H5: identify the MVP-leader within this team — the player with the
    // strictly highest MVP count. Ties produce no badge so we don't show
    // multiple "MVP" labels on a single team.
    let mvpLeaderName: string | null = null;
    let mvpLeaderCount = 0;
    let mvpLeaderUnique = true;
    for (const e of entries) {
      if (e.mvps > mvpLeaderCount) {
        mvpLeaderCount = e.mvps;
        mvpLeaderName = e.name;
        mvpLeaderUnique = true;
      } else if (e.mvps === mvpLeaderCount && e.mvps > 0 && e.name !== mvpLeaderName) {
        mvpLeaderUnique = false;
      }
    }
    const showMvpBadge = mvpLeaderCount > 0 && mvpLeaderUnique;

    // ── Team header row ──
    const headerRow = document.createElement('tr');
    headerRow.className = 'sb-team-header';
    headerRow.style.cssText = `background:${colors.header};`;
    const headerTd = document.createElement('td');
    headerTd.colSpan = COLUMNS.length;
    headerTd.style.cssText = 'padding:4px 8px;font-weight:bold;font-size:11px;letter-spacing:1px;color:#ddd;';
    headerTd.textContent = colors.label;
    headerRow.appendChild(headerTd);
    this.bodyEl.appendChild(headerRow);

    // ── Column headers row ──
    const colRow = document.createElement('tr');
    colRow.className = 'sb-col-header';
    colRow.style.cssText = 'background:rgba(0,0,0,0.3);border-bottom:1px solid #444;';
    for (const col of COLUMNS) {
      const th = document.createElement('td');
      th.style.cssText = `text-align:${col.align};padding:2px 4px;font-size:10px;color:#888;font-weight:600;min-width:${col.minWidth}px;`;
      if (col.width.includes(' ')) {
        th.style.flex = col.width;
      } else {
        th.style.width = col.width;
      }
      th.textContent = col.label;
      colRow.appendChild(th);
    }
    this.bodyEl.appendChild(colRow);

    // ── Player rows ──
    for (const entry of entries) {
      const row = document.createElement('tr');
      row.className = 'sb-player-row';
      row.style.cssText = `background:${colors.bg};border-bottom:1px solid #333;`;

      if (entry.isLocal) {
        row.style.background = team === 1
          ? 'rgba(222, 163, 67, 0.25)'
          : team === 2
            ? 'rgba(93, 160, 224, 0.25)'
            : 'rgba(233, 69, 96, 0.25)';
      }

      // Column 0: Weapon icon
      const weaponTd = this.createCell('center', '24px');
      weaponTd.style.fontSize = '9px';
      weaponTd.style.color = '#aaa';
      weaponTd.style.fontWeight = '600';
      weaponTd.textContent = WEAPON_SHORT[entry.weapon] ?? entry.weapon.substring(0, 3).toUpperCase();
      row.appendChild(weaponTd);

      // Column 1: Name (with MVP stars)
      const nameTd = this.createCell('left', '1 1 0');
      nameTd.style.minWidth = '100px';
      nameTd.style.overflow = 'hidden';
      nameTd.style.textOverflow = 'ellipsis';
      nameTd.style.whiteSpace = 'nowrap';
      if (entry.mvps > 0) {
        const starSpan = document.createElement('span');
        starSpan.style.cssText = 'color:#ffd700;margin-right:3px;font-size:11px;';
        starSpan.textContent = '\u2605'.repeat(Math.min(entry.mvps, 5)); // gold stars, cap display at 5
        nameTd.appendChild(starSpan);
      }
      const nameSpan = document.createElement('span');
      nameSpan.textContent = entry.name;
      nameTd.appendChild(nameSpan);
      row.appendChild(nameTd);

      // Column 2: Kills
      const killsTd = this.createCell('center', '40px');
      killsTd.textContent = String(entry.kills);
      killsTd.style.fontWeight = '600';
      row.appendChild(killsTd);

      // Column 3: Assists
      const assistsTd = this.createCell('center', '40px');
      assistsTd.textContent = String(entry.assists);
      row.appendChild(assistsTd);

      // Column 4: Deaths
      const deathsTd = this.createCell('center', '40px');
      deathsTd.textContent = String(entry.deaths);
      row.appendChild(deathsTd);

      // Column 5: ADR (color-coded)
      const adrTd = this.createCell('center', '48px');
      adrTd.textContent = String(Math.round(entry.adr));
      adrTd.style.color = adrColor(entry.adr);
      adrTd.style.fontWeight = '600';
      row.appendChild(adrTd);

      // Column 6: HS%
      const hsTd = this.createCell('center', '48px');
      hsTd.textContent = `${entry.hsPercent}%`;
      row.appendChild(hsTd);

      // Column 7: Flash Assists
      const faTd = this.createCell('center', '36px');
      faTd.textContent = String(entry.flashAssists);
      faTd.style.color = entry.flashAssists > 0 ? '#fff176' : '#666';
      row.appendChild(faTd);

      // Column 8: Utility Damage
      const udTd = this.createCell('center', '40px');
      udTd.textContent = String(entry.utilityDamage);
      udTd.style.color = entry.utilityDamage > 0 ? '#ce93d8' : '#666';
      row.appendChild(udTd);

      // Column 9: Ping
      const pingTd = this.createCell('center', '48px');
      pingTd.style.color = '#888';
      pingTd.textContent = `${entry.ping}ms`;
      row.appendChild(pingTd);

      // Column 10: Achievement badges (UI-H5)
      const achTd = this.createCell('center', '120px');
      achTd.setAttribute('data-achievements', '1');
      this.renderAchievementBadges(
        achTd,
        entry,
        showMvpBadge && entry.name === mvpLeaderName,
      );
      row.appendChild(achTd);

      // Column 11: Mute/Report actions (hidden for local player)
      const actionsTd = this.createCell('center', '80px');
      if (!entry.isLocal) {
        this.renderActionButtons(actionsTd, entry.name);
      }
      row.appendChild(actionsTd);

      this.bodyEl.appendChild(row);
    }

    // ── Team economy footer ──
    const ecoRow = document.createElement('tr');
    ecoRow.className = 'sb-team-eco';
    ecoRow.style.cssText = `background:${colors.header};border-bottom:2px solid #555;`;
    const ecoTd = document.createElement('td');
    ecoTd.colSpan = COLUMNS.length;
    ecoTd.style.cssText = 'padding:3px 8px;font-size:11px;text-align:right;color:#ccc;';
    const total = teamEconomyTotal(entries);
    ecoTd.textContent = `Team Economy: $${total.toLocaleString()}`;
    ecoRow.appendChild(ecoTd);
    this.bodyEl.appendChild(ecoRow);
  }

  private shouldShowSurrenderVote(): boolean {
    return this.matchState === 'playing' && (this.roundNumber === null || this.roundNumber >= 12);
  }

  private renderSurrenderFooter(): void {
    if (!this.shouldShowSurrenderVote()) return;

    const row = document.createElement('tr');
    row.className = 'sb-footer-row';
    const td = document.createElement('td');
    td.colSpan = COLUMNS.length;
    td.style.cssText = 'padding:8px;text-align:right;background:rgba(0,0,0,0.35);border-top:1px solid #444;';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sb-action-btn sb-surrender-btn';
    btn.textContent = 'VOTE TO SURRENDER';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const confirmed = window.confirm('Start a team vote to surrender?');
      if (!confirmed) return;
      document.dispatchEvent(new CustomEvent('surrender-vote-initiate', { bubbles: true }));
    });
    td.appendChild(btn);
    row.appendChild(td);
    this.bodyEl.appendChild(row);
  }

  /**
   * UI-H5: Render highlight-reel badges (MVP / 1vN / Ace / Longest) inline.
   * Only shows badges that apply — empty cell when none.
   */
  private renderAchievementBadges(
    td: HTMLTableCellElement,
    entry: EnhancedScoreboardEntry,
    isMvpLeader: boolean,
  ): void {
    const badges: Array<{ label: string; color: string; cls: string; title: string }> = [];

    if (isMvpLeader) {
      badges.push({
        label: 'MVP',
        color: '#ffd700',
        cls: 'sb-badge-mvp',
        title: `${entry.name}: most MVPs (${entry.mvps})`,
      });
    }
    const clutchCount = entry.clutches ?? 0;
    if (clutchCount > 0) {
      badges.push({
        label: clutchCount > 1 ? `1vN x${clutchCount}` : '1vN',
        color: '#ef5350',
        cls: 'sb-badge-clutch',
        title: `${entry.name}: ${clutchCount} clutch win${clutchCount > 1 ? 's' : ''}`,
      });
    }
    const aceCount = entry.aces ?? 0;
    if (aceCount > 0) {
      badges.push({
        label: aceCount > 1 ? `Ace x${aceCount}` : 'Ace',
        color: '#ce93d8',
        cls: 'sb-badge-ace',
        title: `${entry.name}: ${aceCount} ace${aceCount > 1 ? 's' : ''}`,
      });
    }
    const longestShot = entry.longestShot ?? 0;
    if (longestShot > 0) {
      // Round to whole metres — the underlying value is already a metric
      // distance from the kill-shot raycast.
      const m = Math.round(longestShot);
      badges.push({
        label: `${m}m`,
        color: '#4caf50',
        cls: 'sb-badge-longest',
        title: `${entry.name}: longest kill-shot ${m}m`,
      });
    }

    if (badges.length === 0) return;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;gap:2px;';
    for (const b of badges) {
      const span = document.createElement('span');
      span.className = `sb-badge ${b.cls}`;
      span.textContent = b.label;
      span.title = b.title;
      span.style.cssText =
        `display:inline-block;padding:1px 5px;border-radius:3px;` +
        `font-size:9px;font-weight:600;letter-spacing:0.5px;` +
        `background:${b.color}22;border:1px solid ${b.color}88;color:${b.color};`;
      wrapper.appendChild(span);
    }
    td.appendChild(wrapper);
  }

  /** Render mute and report buttons inside the given cell. */
  private renderActionButtons(td: HTMLTableCellElement, playerName: string): void {
    const isMuted = this.mutedPlayers.has(playerName);
    const isReported = this.reportedPlayers.has(playerName);

    // Mute button
    const muteBtn = document.createElement('button');
    muteBtn.className = 'sb-action-btn sb-mute-btn';
    muteBtn.title = isMuted ? `Unmute ${playerName}` : `Mute ${playerName}`;
    muteBtn.textContent = isMuted ? '\u{1F507}' : '\u{1F50A}';
    if (isMuted) {
      muteBtn.classList.add('sb-muted');
    }
    muteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.mutedPlayers.has(playerName)) {
        this.mutedPlayers.delete(playerName);
      } else {
        this.mutedPlayers.add(playerName);
      }
      // Re-render button state inline (avoid full scoreboard rebuild)
      const nowMuted = this.mutedPlayers.has(playerName);
      muteBtn.textContent = nowMuted ? '\u{1F507}' : '\u{1F50A}';
      muteBtn.title = nowMuted ? `Unmute ${playerName}` : `Mute ${playerName}`;
      muteBtn.classList.toggle('sb-muted', nowMuted);
    });
    td.appendChild(muteBtn);

    // Report button
    const reportBtn = document.createElement('button');
    reportBtn.className = 'sb-action-btn sb-report-btn';
    reportBtn.title = isReported ? `Already reported ${playerName}` : `Report ${playerName}`;
    reportBtn.textContent = '\u26A0';
    if (isReported) {
      reportBtn.classList.add('sb-reported');
      reportBtn.disabled = true;
    }
    reportBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.reportedPlayers.has(playerName)) return;
      this.showReportMenu(reportBtn, playerName);
    });
    td.appendChild(reportBtn);
  }

  /** Show a dropdown menu for selecting a report reason. */
  private showReportMenu(anchor: HTMLButtonElement, playerName: string): void {
    // Remove any existing report menu
    const existing = document.querySelector('.sb-report-menu');
    if (existing) existing.remove();

    const menu = document.createElement('div');
    menu.className = 'sb-report-menu';

    const title = document.createElement('div');
    title.className = 'sb-report-menu-title';
    title.textContent = `Report ${playerName}`;
    menu.appendChild(title);

    for (const reason of REPORT_REASONS) {
      const item = document.createElement('button');
      item.className = 'sb-report-menu-item';
      item.textContent = reason.charAt(0).toUpperCase() + reason.slice(1);
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.reportedPlayers.add(playerName);
        // Update button state
        anchor.classList.add('sb-reported');
        anchor.disabled = true;
        anchor.title = `Already reported ${playerName}`;
        if (this.onReport) {
          this.onReport(playerName, reason);
        }
        menu.remove();
      });
      menu.appendChild(item);
    }

    // Position the menu near the anchor button
    const rect = anchor.getBoundingClientRect();
    menu.style.position = 'fixed';
    menu.style.left = `${rect.left}px`;
    menu.style.top = `${rect.bottom + 2}px`;
    menu.style.zIndex = '10001';
    document.body.appendChild(menu);

    // Close menu when clicking elsewhere
    const closeHandler = (ev: MouseEvent) => {
      if (!menu.contains(ev.target as Node)) {
        menu.remove();
        document.removeEventListener('click', closeHandler, true);
      }
    };
    // Defer so this click event doesn't immediately close the menu
    setTimeout(() => document.addEventListener('click', closeHandler, true), 0);
  }

  /** Create a table cell with standard styling. */
  private createCell(align: string, widthOrFlex: string): HTMLTableCellElement {
    const td = document.createElement('td');
    td.style.cssText = `text-align:${align};padding:3px 4px;font-size:12px;`;
    if (widthOrFlex.includes(' ')) {
      td.style.flex = widthOrFlex;
    } else {
      td.style.width = widthOrFlex;
    }
    return td;
  }

  /** Inject scoped CSS for responsive scoreboard layout. */
  private injectStyles(): void {
    if (document.getElementById('sb-enhanced-styles')) return;
    const style = document.createElement('style');
    style.id = 'sb-enhanced-styles';
    style.textContent = `
      #scoreboard {
        max-width: min(90vw, 720px);
        min-width: 480px;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      }
      #scoreboard table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
      }
      .sb-player-row:hover {
        filter: brightness(1.2);
      }
      .sb-action-btn {
        background: rgba(255,255,255,0.08);
        border: 1px solid rgba(255,255,255,0.15);
        color: #ccc;
        font-size: 11px;
        padding: 1px 4px;
        margin: 0 1px;
        cursor: pointer;
        border-radius: 3px;
        line-height: 1.2;
        vertical-align: middle;
        transition: background 0.15s, border-color 0.15s;
        touch-action: manipulation;
      }
      .sb-action-btn:hover {
        background: rgba(255,255,255,0.18);
        border-color: rgba(255,255,255,0.3);
      }
      .sb-action-btn:disabled {
        opacity: 0.4;
        cursor: default;
      }
      .sb-surrender-btn {
        padding: 6px 12px;
        border-color: rgba(239,83,80,0.45);
        background: rgba(239,83,80,0.16);
        color: #ff9a9a;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 1px;
      }
      .sb-surrender-btn:hover {
        border-color: rgba(239,83,80,0.75);
        background: rgba(239,83,80,0.28);
        color: #fff;
      }
      .sb-mute-btn.sb-muted {
        background: rgba(239,83,80,0.25);
        border-color: rgba(239,83,80,0.5);
        color: #ef5350;
      }
      .sb-report-btn.sb-reported {
        background: rgba(255,167,38,0.2);
        border-color: rgba(255,167,38,0.4);
        color: #ffa726;
      }
      .sb-report-menu {
        background: rgba(30,30,30,0.97);
        border: 1px solid #555;
        border-radius: 4px;
        padding: 4px 0;
        min-width: 130px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      }
      .sb-report-menu-title {
        padding: 4px 10px;
        font-size: 11px;
        font-weight: 600;
        color: #ffa726;
        border-bottom: 1px solid #444;
        margin-bottom: 2px;
      }
      .sb-report-menu-item {
        display: block;
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        color: #ccc;
        font-size: 12px;
        padding: 5px 10px;
        cursor: pointer;
        font-family: inherit;
      }
      .sb-report-menu-item:hover {
        background: rgba(255,255,255,0.1);
        color: #fff;
      }
      /* BUG-PARITY-H3: touch-friendly spectator controls (mobile only). */
      .sb-spec-btn {
        background: rgba(255,255,255,0.10);
        border: 1px solid rgba(255,255,255,0.25);
        color: #eee;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.5px;
        padding: 10px 14px;
        min-width: 72px;
        min-height: 40px;
        cursor: pointer;
        border-radius: 4px;
        touch-action: manipulation;
        transition: background 0.15s, border-color 0.15s, transform 0.05s;
      }
      .sb-spec-btn:hover {
        background: rgba(255,255,255,0.18);
        border-color: rgba(255,255,255,0.4);
      }
      .sb-spec-btn:active {
        transform: scale(0.96);
        background: rgba(255,255,255,0.25);
      }
      .sb-spec-mode {
        background: rgba(93, 160, 224, 0.18);
        border-color: rgba(93, 160, 224, 0.45);
        color: #9bcaf0;
      }
      .sb-spec-mode:hover {
        background: rgba(93, 160, 224, 0.28);
      }
      @media (max-width: 640px) {
        /* BUG-P-18 / BUG-P-18: drop the hard 320px floor; allow the
           scoreboard to fill the viewport and scroll horizontally on very
           narrow phones rather than overflow the screen. */
        #scoreboard {
          min-width: 0;
          max-width: calc(100vw - 16px);
          font-size: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        #scoreboard td {
          padding: 2px 2px !important;
          font-size: 10px !important;
        }
        /* Hide niche columns on phones to keep the core K/A/D readable.
           Columns (1-indexed) are: 1 icon, 2 Name, 3 K, 4 A, 5 D, 6 ADR,
           7 HS%, 8 FA, 9 UD, 10 Ping, 11 Hi, 12 actions. */
        #scoreboard th:nth-child(8),
        #scoreboard td:nth-child(8),
        #scoreboard th:nth-child(9),
        #scoreboard td:nth-child(9),
        #scoreboard th:nth-child(11),
        #scoreboard td:nth-child(11) {
          display: none !important;
        }
        /* BUG-P-18: bump action button tap target on phones. */
        .sb-action-btn {
          font-size: 13px;
          padding: 8px 12px;
          min-width: 36px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Show the scoreboard. Optional {@link opts.spectating} drives the mobile
   * PREV/MODE/NEXT row (BUG-PARITY-H3). Backward compatible — `show()` with
   * no args preserves prior behaviour.
   */
  show(opts?: { spectating?: boolean }): void {
    if (opts && typeof opts.spectating === 'boolean') {
      this.spectating = opts.spectating;
    }
    this.el.style.display = 'block';
    this.visible = true;
  }

  /** Set the spectating flag without showing — for state changes while open. */
  setSpectating(spectating: boolean): void {
    this.spectating = spectating;
  }

  hide(): void {
    this.el.style.display = 'none';
    this.visible = false;
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }
}
